// Auth sheet — the modal that appears when a player wants to save
// Matches the e-debiyat.com/giris flow: Google | Apple | Email
// Email path: enter email → check → login or register

import {
  checkEmail, signInWithEmail, signUpWithEmail,
  sendPasswordReset, startOAuthRedirect, finishOAuthRedirect,
  onAuthChange, getUser, AuthError,
} from './auth';
import { toast } from './ui';

// ─── Types ────────────────────────────────────────────────────────────────────

type AuthCallback = () => void;

// ─── HTML ─────────────────────────────────────────────────────────────────────

const SHEET_ID = 'sheet-auth';

function injectSheet(): void {
  if (document.getElementById(SHEET_ID)) return;

  const sheet = document.createElement('div');
  sheet.id        = SHEET_ID;
  sheet.className = 'sheet';
  sheet.setAttribute('aria-hidden', 'true');
  sheet.innerHTML = `
    <div class="sheet__backdrop" id="auth-backdrop"></div>
    <div class="sheet__content card" id="auth-content">
      <div class="sheet__handle"></div>
      <div id="auth-panel-root"></div>
    </div>
  `;
  document.getElementById('app')!.appendChild(sheet);

  document.getElementById('auth-backdrop')!.addEventListener('click', closeSheet);
}

// ─── Panels ───────────────────────────────────────────────────────────────────

function renderProviderPanel(): void {
  const root = document.getElementById('auth-panel-root')!;
  root.innerHTML = `
    <h2 class="text-bold text-lg">Save your score</h2>
    <p class="text-muted text-sm mt-2">Sign in to claim your spot on the leaderboard</p>

    <div class="flex flex-col gap-3 mt-6">
      <button class="btn btn--outline" id="auth-google">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
          <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>

      <button class="btn auth-apple-btn" id="auth-apple">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
          <path d="M14.05 9.5c-.02-1.96 1.6-2.9 1.67-2.95-1.1-1.6-2.68-.6-3.34-.6-1.13-.07-2.2.65-2.78.65-.58 0-1.5-.63-2.47-.61C5.58 6.05 4.2 7.25 3.5 9c-1.4 3.54 1 7.2 2.7 9.2 1.07 1.3 2.3 2.7 3.92 2.7 1.57-.07 2.18-1 4.07-1 1.87 0 2.43 1 4.06.97 1.7-.03 2.8-1.43 3.87-2.73.7-.97 1.36-2.27 1.36-3.6-.03-.02-2.6-1-2.62-3.94-.01 0 .21-.02.19-.1z"/>
          <path d="M12.3 4.17c.83-1.03 1.4-2.47 1.24-3.9-1.2.05-2.65.8-3.5 1.83-.77.9-1.44 2.33-1.25 3.7 1.33.1 2.7-.67 3.51-1.63z"/>
        </svg>
        Continue with Apple
      </button>

      <button class="btn btn--primary" id="auth-email">
        ✉️ Continue with Email
      </button>
    </div>

    <p class="text-muted text-sm text-center mt-4" style="line-height:1.5">
      By continuing you agree to our
      <a href="/terms" style="color:var(--c-primary)">Terms</a> &
      <a href="/privacy" style="color:var(--c-primary)">Privacy Policy</a>
    </p>
  `;

  // Apple button style (black)
  const appleBtn = document.getElementById('auth-apple') as HTMLButtonElement;
  Object.assign(appleBtn.style, {
    background: '#000', color: '#fff', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', width: '100%', padding: '16px 24px',
    borderRadius: '999px', fontFamily: 'var(--font)',
    fontSize: 'var(--fs-md)', fontWeight: '700', cursor: 'pointer',
  });

  document.getElementById('auth-google')!.addEventListener('click', () => {
    startOAuthRedirect('google');
  });

  document.getElementById('auth-apple')!.addEventListener('click', () => {
    startOAuthRedirect('apple');
  });

  document.getElementById('auth-email')!.addEventListener('click', () => {
    renderEmailPanel();
  });
}

// ─── Email panel ──────────────────────────────────────────────────────────────

function renderEmailPanel(prefill = ''): void {
  const root = document.getElementById('auth-panel-root')!;
  root.innerHTML = `
    <button class="btn btn--ghost text-sm" id="auth-back" style="width:auto;padding:0;margin-bottom:var(--sp-4)">
      ← Back
    </button>
    <h2 class="text-bold text-lg">Your email</h2>
    <p class="text-muted text-sm mt-2">We'll check if you have an account</p>

    <input
      class="input mt-6"
      id="auth-email-input"
      type="email"
      placeholder="you@example.com"
      autocomplete="email"
      value="${prefill}"
    />
    <p class="text-accent text-sm mt-2" id="auth-email-error" style="display:none"></p>

    <button class="btn btn--primary mt-4" id="auth-email-next">
      Continue
    </button>
  `;

  document.getElementById('auth-back')!.addEventListener('click', renderProviderPanel);

  const input = document.getElementById('auth-email-input') as HTMLInputElement;
  input.focus();

  const nextBtn = document.getElementById('auth-email-next') as HTMLButtonElement;

  const proceed = async () => {
    const email = input.value.trim();
    if (!email || !email.includes('@')) {
      showError('auth-email-error', 'Please enter a valid email address.');
      return;
    }

    nextBtn.disabled    = true;
    nextBtn.textContent = 'Checking…';

    try {
      const mode = await checkEmail(email);
      if (mode === 'login')    renderPasswordPanel(email);
      else                     renderRegisterPanel(email);
    } catch (err) {
      showError('auth-email-error', err instanceof AuthError ? err.message : 'Something went wrong.');
      nextBtn.disabled    = false;
      nextBtn.textContent = 'Continue';
    }
  };

  nextBtn.addEventListener('click', proceed);
  input.addEventListener('keydown', e => { if (e.key === 'Enter') proceed(); });
}

// ─── Password panel (existing user) ──────────────────────────────────────────

function renderPasswordPanel(email: string): void {
  const root = document.getElementById('auth-panel-root')!;
  root.innerHTML = `
    <button class="btn btn--ghost text-sm" id="auth-back" style="width:auto;padding:0;margin-bottom:var(--sp-4)">
      ← Back
    </button>
    <h2 class="text-bold text-lg">Welcome back</h2>
    <p class="text-muted text-sm mt-2">${email}</p>

    <input
      class="input mt-6"
      id="auth-password-input"
      type="password"
      placeholder="Your password"
      autocomplete="current-password"
    />
    <p class="text-accent text-sm mt-2" id="auth-password-error" style="display:none"></p>

    <button class="btn btn--primary mt-4" id="auth-signin-btn">
      Sign In
    </button>
    <button class="btn btn--ghost text-sm mt-2" id="auth-forgot">
      Forgot password?
    </button>
  `;

  document.getElementById('auth-back')!.addEventListener('click', () => renderEmailPanel(email));

  const input    = document.getElementById('auth-password-input') as HTMLInputElement;
  const signinBtn = document.getElementById('auth-signin-btn') as HTMLButtonElement;
  input.focus();

  const proceed = async () => {
    const password = input.value;
    if (!password) return;

    signinBtn.disabled    = true;
    signinBtn.textContent = 'Signing in…';

    try {
      await signInWithEmail(email, password);
      closeSheet();
      onSignInComplete();
    } catch (err) {
      showError('auth-password-error', err instanceof AuthError ? err.message : 'Sign in failed.');
      signinBtn.disabled    = false;
      signinBtn.textContent = 'Sign In';
    }
  };

  signinBtn.addEventListener('click', proceed);
  input.addEventListener('keydown', e => { if (e.key === 'Enter') proceed(); });

  document.getElementById('auth-forgot')!.addEventListener('click', async () => {
    await sendPasswordReset(email);
    toast('Password reset email sent!', 'success');
  });
}

// ─── Register panel (new user) ────────────────────────────────────────────────

function renderRegisterPanel(email: string): void {
  const root = document.getElementById('auth-panel-root')!;
  root.innerHTML = `
    <button class="btn btn--ghost text-sm" id="auth-back" style="width:auto;padding:0;margin-bottom:var(--sp-4)">
      ← Back
    </button>
    <h2 class="text-bold text-lg">Create account</h2>
    <p class="text-muted text-sm mt-2">${email}</p>

    <input
      class="input mt-6"
      id="auth-name-input"
      type="text"
      placeholder="Your name"
      autocomplete="name"
      maxlength="32"
    />
    <input
      class="input mt-3"
      id="auth-newpass-input"
      type="password"
      placeholder="Choose a password (min 6 chars)"
      autocomplete="new-password"
    />
    <p class="text-accent text-sm mt-2" id="auth-register-error" style="display:none"></p>

    <button class="btn btn--primary mt-4" id="auth-register-btn">
      Create Account
    </button>
  `;

  document.getElementById('auth-back')!.addEventListener('click', () => renderEmailPanel(email));

  const nameInput  = document.getElementById('auth-name-input')  as HTMLInputElement;
  const passInput  = document.getElementById('auth-newpass-input') as HTMLInputElement;
  const registerBtn = document.getElementById('auth-register-btn') as HTMLButtonElement;
  nameInput.focus();

  const proceed = async () => {
    const name     = nameInput.value.trim();
    const password = passInput.value;

    if (name.length < 2) {
      showError('auth-register-error', 'Please enter your name.');
      return;
    }
    if (password.length < 6) {
      showError('auth-register-error', 'Password must be at least 6 characters.');
      return;
    }

    registerBtn.disabled    = true;
    registerBtn.textContent = 'Creating account…';

    try {
      await signUpWithEmail(email, password, name);
      closeSheet();
      onSignInComplete();
    } catch (err) {
      showError('auth-register-error', err instanceof AuthError ? err.message : 'Registration failed.');
      registerBtn.disabled    = false;
      registerBtn.textContent = 'Create Account';
    }
  };

  registerBtn.addEventListener('click', proceed);
  passInput.addEventListener('keydown', e => { if (e.key === 'Enter') proceed(); });
}

// ─── Open / close ─────────────────────────────────────────────────────────────

let pendingCallback: AuthCallback | null = null;

export function openAuthSheet(onComplete?: AuthCallback): void {
  injectSheet();
  pendingCallback = onComplete ?? null;

  const sheet = document.getElementById(SHEET_ID)!;
  renderProviderPanel();
  sheet.classList.add('open');
  sheet.removeAttribute('aria-hidden');
}

function closeSheet(): void {
  const sheet = document.getElementById(SHEET_ID);
  sheet?.classList.remove('open');
  sheet?.setAttribute('aria-hidden', 'true');
}

function onSignInComplete(): void {
  toast('Signed in!', 'success');
  pendingCallback?.();
  pendingCallback = null;
}

// ─── Boot: check OAuth redirect on app load ───────────────────────────────────

export async function initAuth(): Promise<void> {
  const user = await finishOAuthRedirect();
  if (user) {
    toast(`Welcome, ${user.displayName ?? user.email}!`, 'success');
    onSignInComplete();
  }
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function showError(id: string, msg: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.textContent    = msg;
  el.style.display  = 'block';
}
